import { useEffect, useMemo, useState } from 'react'
import { PageHeader, SectionHeader, Button, TextInput, TextArea, Badge } from '../../shared/ui'
import {
  useSchuljahr,
  useSchuljahrPolicies,
  useUpdateSchuljahrPolicy,
  type SchuljahrPolicy,
} from '../hr/api/schuljahrApi'

const BUNDESLAENDER = ['HH', 'SH'] as const

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('T')[0].split('-')
  return `${d}.${m}.${y}`
}

function PolicyRow({ bundesland }: { bundesland: string }) {
  const { data: schuljahre } = useSchuljahr(bundesland)
  const { data: policies } = useSchuljahrPolicies(bundesland)
  const update = useUpdateSchuljahrPolicy()

  const existing: SchuljahrPolicy | undefined = policies?.[0]
  const [vonOff, setVonOff] = useState<string>('')
  const [bisOff, setBisOff] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (existing) {
      setVonOff(existing.sommerferienVonOffsetDays != null ? String(existing.sommerferienVonOffsetDays) : '')
      setBisOff(existing.sommerferienBisOffsetDays != null ? String(existing.sommerferienBisOffsetDays) : '')
      setNotes(existing.notes ?? '')
    } else {
      setVonOff(''); setBisOff(''); setNotes('')
    }
    setDirty(false)
  }, [existing])

  async function save() {
    await update.mutateAsync({
      bundesland,
      patch: {
        sommerferienVonOffsetDays: vonOff === '' ? null : Number(vonOff),
        sommerferienBisOffsetDays: bisOff === '' ? null : Number(bisOff),
        notes: notes || null,
      },
    })
    setDirty(false)
  }

  return (
    <section className="rounded-2xl border border-slate-200/60 bg-white p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Badge tone="info">{bundesland}</Badge>
          <SectionHeader label={`Schuljahre ${bundesland}`} count={schuljahre?.length ?? 0} />
        </div>
        {existing?.auditModifiedAt && (
          <span className="text-[10px] uppercase tracking-widest text-slate-400">
            Zuletzt geändert {fmtDate(existing.auditModifiedAt)} · {existing.auditModifiedBy ?? '—'}
          </span>
        )}
      </div>

      {/* FM-sourced Schuljahre, read-only */}
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Schuljahr</th>
              <th className="px-3 py-2 text-left font-medium">Von</th>
              <th className="px-3 py-2 text-left font-medium">Bis</th>
            </tr>
          </thead>
          <tbody>
            {(schuljahre ?? []).map(s => (
              <tr key={s.schuljahr} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{s.schuljahr}</td>
                <td className="px-3 py-2 tabular-nums">{fmtDate(s.von)}</td>
                <td className="px-3 py-2 tabular-nums">{fmtDate(s.bis)}</td>
              </tr>
            ))}
            {(!schuljahre || schuljahre.length === 0) && (
              <tr><td colSpan={3} className="px-3 py-4 text-center text-slate-400">Keine Schuljahre synchronisiert</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sommerferien offsets — intranet-configurable */}
      <div className="space-y-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Sommerferien-Offsets (Tage)</p>
        <p className="text-xs text-slate-500">
          Sommerferien beginnen <strong>N Tage vor</strong> Schuljahr-Ende und enden <strong>N Tage nach</strong> dem nächsten Schuljahr-Start.
          Leer lassen, um den Wert automatisch aus der Lücke zwischen Schuljahren zu berechnen.
        </p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <TextInput
            label="Von-Offset (Tage)"
            size="compact"
            type="number"
            value={vonOff}
            onChange={e => { setVonOff(e.target.value); setDirty(true) }}
            placeholder="auto"
          />
          <TextInput
            label="Bis-Offset (Tage)"
            size="compact"
            type="number"
            value={bisOff}
            onChange={e => { setBisOff(e.target.value); setDirty(true) }}
            placeholder="auto"
          />
        </div>
        <TextArea
          label="Notizen"
          rows={2}
          value={notes}
          onChange={e => { setNotes(e.target.value); setDirty(true) }}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="primary" size="sm" disabled={!dirty} loading={update.isPending} onClick={save}>
          Speichern
        </Button>
      </div>
      {update.error && <p className="text-xs text-rose-600">{String(update.error)}</p>}
    </section>
  )
}

export default function SchuljahrPolicyPage() {
  const rows = useMemo(() => BUNDESLAENDER, [])
  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <PageHeader
        title="Schuljahr & Sommerferien"
        subtitle="FM-synchronisierte Schuljahre pro Bundesland · konfigurierbare Sommerferien-Offsets"
      />
      {rows.map(bl => <PolicyRow key={bl} bundesland={bl} />)}
    </div>
  )
}
